import { parseNum } from './numero.js';
/* custos.ts — resolve UMA configuração de custo em reais para UM projeto.
 *
 * Existia em três cópias no app antigo (dashboard, configurações e PDF) e as
 * três divergiam: a mesma empresa via um custo de instalação no painel e outro
 * no relatório. Agora é uma função só, e é a única que sabe o que "modo" e
 * "faixa" significam.
 */
/**
 * custoConfig(cfg, kwp, mods): valor em R$, ou `null`.
 *
 * `null` tem significado preciso e diferente de zero:
 *   - não configurado (sem `modo`), OU
 *   - configurado por medida (kwp/módulo/faixa) mas o projeto ainda não tem
 *     essa medida — não dá para multiplicar por um número que o usuário não
 *     digitou.
 *
 * Quem chama decide o que fazer com o null. `montarParametros` converte em 0,
 * porque custo sem configuração vale ZERO no preço — nunca um valor inventado.
 * Já a tela de Indicadores usa o null para escrever "R$ 350 por kWp" em vez de
 * um número falso. Se esta função devolvesse 0, essa distinção sumiria.
 */
export function custoConfig(cfg, kwp, mods) {
    if (!cfg || !cfg.modo)
        return null;
    if (cfg.modo === 'fixo')
        return parseNum(cfg.taxa);
    if (cfg.modo === 'modulo')
        return mods > 0 ? parseNum(cfg.taxa) * mods : null;
    if (cfg.modo === 'kwp')
        return kwp > 0 ? parseNum(cfg.taxa) * kwp : null;
    if (cfg.modo === 'faixa') {
        const faixas = cfg.faixas ?? [];
        if (faixas.length === 0)
            return null;
        const base = cfg.faixaVariavel === 'modulos' ? mods : kwp;
        if (!(base > 0))
            return null;
        // A tela deixa o usuário criar/editar faixas fora de ordem, então ordenamos
        // aqui em vez de confiar na ordem do jsonb. `slice()` antes de `sort()`
        // porque sort é destrutivo e o array veio de fora — mutar entrada do
        // chamador é o tipo de efeito colateral que uma função pura não pode ter.
        const ordenadas = faixas.slice().sort((a, b) => limite(a) - limite(b));
        let escolhida;
        for (const faixa of ordenadas) {
            if (base <= limite(faixa)) {
                escolhida = faixa;
                break;
            }
        }
        // Nenhum degrau cobre o projeto (todas as faixas são fechadas e o sistema é
        // maior que a última): usa a última, a maior. Preferimos cobrar a faixa
        // mais alta a devolver "não sei" para um projeto grande.
        if (!escolhida)
            escolhida = ordenadas[ordenadas.length - 1];
        if (!escolhida)
            return null; // inalcançável (faixas.length > 0); satisfaz o compilador
        const taxa = parseNum(escolhida.taxa);
        // `||` e não `??`: um subModo vazio ('' gravado por versão antiga da tela)
        // tem de cair no padrão 'fixo', igual a ausente.
        const sub = cfg.subModo || 'fixo';
        if (sub === 'modulo')
            return mods > 0 ? taxa * mods : null;
        if (sub === 'kwp')
            return kwp > 0 ? taxa * kwp : null;
        return taxa;
    }
    return null;
}
/**
 * Limite superior de um degrau. `ate` null / 0 / vazio = faixa ABERTA.
 *
 * Tratar 0 como aberto é deliberado: 0 é o que sobra quando o usuário limpa o
 * campo, e uma faixa com limite 0 nunca casaria com projeto nenhum — viraria
 * custo faltando no preço, silenciosamente. Aberto é a leitura segura.
 */
function limite(f) {
    const v = f.ate == null ? 0 : parseNum(f.ate);
    return v > 0 ? v : Infinity;
}
//# sourceMappingURL=custos.js.map